import React from 'react';
import {
  WhiteInstantSearchBuilder,
  CurrentRefinements,
  NoResults,
  Pagination,
  RefinementListPanel,
  SearchBox,
  SortBy,
  MenuPanel,
} from '@white-nl/instantsearch-components';

import { CheckSharpSolid, MagnifyingGlassSharpRegular, XmarkSharpRegular } from '../components/ui/icons';
import { DropdownIndicator } from '../components/ui/React-select';
import Stats from '../components/ui/metadata/Stats';
import { Hit as DownloadHit } from '../components/downloads/results/Hit';
import Loading from '../components/downloads/results/Loading';
import LanguageSelection from '../components/downloads/refinements/LanguageSelection';

const settings = document.getElementById('section-filters')?.dataset || {};
const whiteInstantSearchDownloads = new WhiteInstantSearchBuilder();
whiteInstantSearchDownloads
  .setHitsPerPage(24)
  .addAdditionalData({ hitsPerPage: 24 })
  .addSearchingComponent([
    {
      settings: {
        classNames: {
          submit: 'submit',
          reset: 'reset',
          loadingIndicator: 'reset',
          input: 'form-control',
        },
        submitIconComponent: () => (
          <MagnifyingGlassSharpRegular />
        ),
        resetIconComponent: () => (
          <XmarkSharpRegular />
        ),
        placeholder: Craft.t('downloads', 'Find your download'),
        translations: {
          submitButtonTitle: Craft.t('downloads', 'Submit the search query'),
          resetButtonTitle: Craft.t('downloads', 'Clear the search query'),
        },
      },
      component: SearchBox,
    },
  ])
  .addHitComponent([
    {
      settings: {
        loading: {
          translations: {
            loading: Craft.t('downloads', 'Loading downloads...'),
          },
        },
        noResults: {
          translations: {
            noResults: Craft.t('downloads', 'Sorry, we can\'t find any downloads!'),
            refinements: Craft.t('downloads', 'Try to reset your applied filters.'),
            query: Craft.t('downloads', 'Please try another query.'),
          },
        },
      },
      component: DownloadHit,
      loadingComponent: Loading,
      noResultsComponent: NoResults,
    },
  ])
  .addMenu([
    {
      settings: {
        attribute: 'category',
        classNames: {
          root: 'filters-list',
          count: 'filters-count',
          selectedItem: 'active',
        },
        transformItems: (items) => items.map((item) => {
          // eslint-disable-next-line @typescript-eslint/dot-notation
          const { downloadCategoryTitles } = whiteInstantSearchDownloads['additionalData'];

          if (item.value in downloadCategoryTitles) {
            const newLabel = downloadCategoryTitles[item.value];
            return Object.assign(item, { label: newLabel });
          }

          if (!item.value) {
            const newLabel = Craft.t('downloads', 'All downloads');
            return Object.assign(item, { label: newLabel });
          }

          return item;
        }),
        sortBy: (a, b) => {
          // eslint-disable-next-line @typescript-eslint/dot-notation
          const { downloadCategoryPositions } = whiteInstantSearchDownloads['additionalData'];

          const positionA = downloadCategoryPositions[a.name] || -1;
          const positionB = downloadCategoryPositions[b.name] || -1;

          if (positionA > positionB) {
            return 1;
          }
          if (positionA < positionB) {
            return -1;
          }

          return 0;
        },
        limit: 50,
        panelSettings: {
          collapsiblePanelSettings: {
            classNames: {
              root: 'form-wrap',
              header: 'filter-title h4',
            },
            defaultExpanded: true,
            canCollapse: true,
          },
          header: Craft.t('downloads', 'Category'),
        },
      },
      component: MenuPanel,
    },
  ])
  .addRefinementList([
    {
      settings: {
        attribute: 'type',
        transformItems: (items) => items.map((item) => {
          // eslint-disable-next-line @typescript-eslint/dot-notation
          const { downloadTypeTitles } = whiteInstantSearchDownloads['additionalData'];

          if (item.value in downloadTypeTitles) {
            const newLabel = downloadTypeTitles[item.value];
            return Object.assign(item, { label: newLabel });
          }

          return item;
        }),
        sortBy: (a, b) => {
          // eslint-disable-next-line @typescript-eslint/dot-notation
          const { downloadTypePositions } = whiteInstantSearchDownloads['additionalData'];

          const positionA = downloadTypePositions[a.name] || -1;
          const positionB = downloadTypePositions[b.name] || -1;

          if (positionA > positionB) {
            return 1;
          }
          if (positionA < positionB) {
            return -1;
          }

          return 0;
        },
        classNames: {
          label: 'custom-control custom-checkbox',
          checkbox: 'custom-control-input',
          labelText: 'custom-control-label',
          showMore: 'btn btn-link btn-sm',
          disabledShowMore: 'd-none',
          count: 'filters-count',
        },
        showMore: true,
        translations: {
          showMoreButtonText({ isShowingMore }) {
            return isShowingMore ? Craft.t('catalog', 'Show less') : Craft.t('downloads', 'Show more');
          },
        },
        limit: 50,
        showMoreLimit: 100,
        panelSettings: {
          collapsiblePanelSettings: {
            classNames: {
              root: 'form-wrap',
              header: 'filter-title h4',
            },
            defaultExpanded: true,
            canCollapse: true,
          },
          header: Craft.t('downloads', 'Document Type'),
        },
      },
      component: RefinementListPanel,
    },
  ])
  .addMenu([
    {
      settings: {
        attribute: 'siteId',
        sortBy: ['name:asc'],
        limit: 30,
        panelSettings: {
          collapsiblePanelSettings: {
            classNames: {
              root: 'form-wrap downloads-language-selection',
              header: 'filter-title h4',
            },
            defaultExpanded: true,
            canCollapse: false,
          },
          header: Craft.t('downloads', 'Language selection'),
        },
      },
      component: LanguageSelection,
    },
  ])
  .addCurrentRefinementsComponent([
    {
      settings: {
        excludedAttributes: ['query', 'siteId'],
        transformItems: (items) => {
          // eslint-disable-next-line @typescript-eslint/dot-notation
          const { downloadCategoryTitles } = whiteInstantSearchDownloads['additionalData'];
          // eslint-disable-next-line @typescript-eslint/dot-notation
          const { downloadTypeTitles } = whiteInstantSearchDownloads['additionalData'];

          return items.map((item) => ({
            ...item,
            refinements: item.refinements.map((refinement) => {
              if (refinement.attribute === 'type' && refinement.value in downloadTypeTitles) {
                return {
                  ...refinement,
                  label: downloadTypeTitles[refinement.value],
                };
              }
              if (refinement.attribute === 'category' && refinement.value in downloadCategoryTitles) {
                return {
                  ...refinement,
                  label: downloadCategoryTitles[refinement.value],
                };
              }

              return refinement;
            }),
          }));
        },
        translations: {
          type: Craft.t('downloads', 'Document Type'),
          category: Craft.t('downloads', 'Category'),
        },
        clearRefinementsSettings: {
          translations: {
            clearAll: Craft.t('downloads', 'Clear all'),
          },
        },
      },
      component: CurrentRefinements,
    },
  ])
  .addPaginationComponent([
    {
      settings: {
        showFirst: false,
        showLast: false,
        classNames: {
          list: 'pagination',
          item: 'page-item',
          previousPageItem: 'previous',
          selectedItem: 'active',
          disabledItem: 'disabled',
          nextPageItem: 'next',
          link: 'page-link',
        },
        translations: {
          previousPageItemText: Craft.t('pagination', 'Previous'),
          previousPageItemAriaLabel: Craft.t('pagination', 'Go to previous page'),
          nextPageItemText: Craft.t('pagination', 'Next'),
          nextPageItemAriaLabel: Craft.t('pagination', 'Go to next page'),
        },
      },
      component: Pagination,
    },
  ])
  .addStatsComponent([
    {
      settings: {
        translations: {
          resultsFound: Craft.t('downloads', 'results found'),
          resultFound: Craft.t('downloads', 'result found'),
        },
      },
      component: Stats,
    },
  ])
  .addSortByComponent([
    {
      settings: {
        items: [
          { label: Craft.t('downloads', 'Most relevant first'), value: settings.mappingName || '' },
          {
            label: Craft.t('downloads', 'Alphabet A-Z'),
            value: `${settings.mappingName || ''}_title_asc`,
          },
          {
            label: Craft.t('downloads', 'Alphabet Z-A'),
            value: `${settings.mappingName || ''}_title_desc`,
          },
        ],
        unstyled: true,
        components: {
          DropdownIndicator,
        },
      },
      component: SortBy,
    },
  ])
  .addCurrentRefinementsComponent(
    [{
      component: ({ currentRefinements }) => (
        currentRefinements.items.reduce((a, b) => a + b.refinements.length, 0) > 0
        && (<CheckSharpSolid iconClass="check-icon" />)
      ),
    }],
    '.filters-icon',
  )
  .addStatsComponent([{ component: ({ stats }) => (`(${stats.nbHits})`) }], '.hits-count')
  .setScrolling({
    includeAttributes: ['page'],
  });
window.whiteInstantSearchDownloads = whiteInstantSearchDownloads;
